import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Search, Filter, Plus, List, Columns, Loader2, Archive, RotateCcw, Eye, Edit, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useCandidatesV2, useArchiveCandidateV2, useRestoreCandidateV2, useMoveCandidateStageV2,
} from '@/hooks/useCandidatesV2';
import CandidateFiltersV2 from './CandidateFiltersV2';
import CreateCandidateModalV2 from './CreateCandidateModalV2';
import EditCandidateModalV2 from './EditCandidateModalV2';
import CandidateDrawerV2 from './CandidateDrawerV2';
import CandidatePipelineBoardV2 from './CandidatePipelineBoardV2';

const STAGES = ['applied','resume_parsed','eligible','exam_assigned','exam_completed','shortlisted','interview_scheduled','offer','hired','rejected'];
const STAGE_LABELS = {
  applied: 'Applied', resume_parsed: 'Resume Parsed', eligible: 'Eligible',
  exam_assigned: 'Exam Assigned', exam_completed: 'Exam Completed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview Scheduled',
  offer: 'Offer', hired: 'Hired', rejected: 'Rejected',
};
const STAGE_COLORS = {
  applied: 'bg-gray-100 text-gray-700',
  resume_parsed: 'bg-blue-100 text-blue-700',
  eligible: 'bg-teal-100 text-teal-700',
  exam_assigned: 'bg-purple-100 text-purple-700',
  exam_completed: 'bg-indigo-100 text-indigo-700',
  shortlisted: 'bg-orange-100 text-orange-700',
  interview_scheduled: 'bg-yellow-100 text-yellow-700',
  offer: 'bg-green-100 text-green-700',
  hired: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const ROWS_OPTIONS = [5, 10, 20, 30, 50];
const EMPTY_FILTERS = { stage: '', owner: '', source: '', status: '', dateFrom: '', dateTo: '' };

const StageBadge = ({ stage }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STAGE_COLORS[stage] || 'bg-gray-100 text-gray-600'}`}>
    {STAGE_LABELS[stage] || stage}
  </span>
);

const fmtDate = (d) => { try { return d ? format(new Date(d), 'MMM dd, yy') : '—'; } catch { return '—'; } };

// Row actions dropdown
const ActionsDropdown = ({ candidate, onView, onEdit, onArchive, onRestore, onMoveStage }) => {
  const [open, setOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setStageOpen(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center gap-1 text-xs px-2 py-1 rounded border hover:bg-gray-50"
        onClick={() => setOpen((v) => !v)}
      >
        Actions <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-20 min-w-[140px] py-1">
          <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2" onClick={() => { onView(); setOpen(false); }}>
            <Eye className="h-3 w-3" /> View
          </button>
          <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2" onClick={() => { onEdit(); setOpen(false); }}>
            <Edit className="h-3 w-3" /> Edit
          </button>
          {candidate.status === 'archived' ? (
            <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 text-green-600" onClick={() => { onRestore(); setOpen(false); }}>
              <RotateCcw className="h-3 w-3" /> Restore
            </button>
          ) : (
            <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 text-orange-600" onClick={() => { onArchive(); setOpen(false); }}>
              <Archive className="h-3 w-3" /> Archive
            </button>
          )}
          <div className="border-t my-1" />
          <div className="relative">
            <button
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between"
              onClick={() => setStageOpen((v) => !v)}
            >
              Move Stage <ChevronDown className="h-3 w-3" />
            </button>
            {stageOpen && (
              <div className="absolute right-full top-0 mr-1 bg-white border rounded-lg shadow-lg z-30 min-w-[160px] py-1 max-h-60 overflow-y-auto">
                {STAGES.filter((s) => s !== candidate.stage).map((s) => (
                  <button
                    key={s}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50"
                    onClick={() => { onMoveStage(s); setOpen(false); setStageOpen(false); }}
                  >
                    {STAGE_LABELS[s]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 11 }).map((_, i) => (
      <td key={i} className="px-3 py-3">
        <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + (i % 3) * 20}%` }} />
      </td>
    ))}
  </tr>
);

const CandidateListV2 = () => {
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy] = useState('createdAt');
  const [sortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editCandidate, setEditCandidate] = useState(null);
  const [drawerCandidateId, setDrawerCandidateId] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = {
    page, limit: rowsPerPage,
    search: debouncedSearch || undefined,
    sortBy, sortOrder,
    stage: filters.stage || undefined,
    owner: filters.owner || undefined,
    source: filters.source || undefined,
    status: filters.status || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
  };

  const { data, isLoading } = useCandidatesV2(queryParams);
  const archiveMutation = useArchiveCandidateV2();
  const restoreMutation = useRestoreCandidateV2();
  const moveStageMutation = useMoveCandidateStageV2();

  const candidates = data?.candidates || data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  const startIdx = (page - 1) * rowsPerPage + 1;
  const endIdx = Math.min(page * rowsPerPage, total);

  const handleApplyFilters = (f) => { setFilters(f); setPage(1); };
  const handleClearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  return (
    <>
      <div className="admin-page-container admin-fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div className="admin-page-header">
          <div className="admin-header-top">
            <h1 className="admin-page-title">Candidates</h1>
            <span className="admin-stat-chip admin-stat-total">Total: {total}</span>
          </div>
          <div className="admin-header-bottom">
            <div className="admin-actions-section" style={{ marginLeft: 'auto' }}>
              {/* Search */}
              <div className="admin-header-search">
                <div className="admin-search-input-wrapper-header">
                  <input
                    type="search"
                    placeholder="Search candidates..."
                    className="admin-search-input-header"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Search className="w-4 h-4 admin-search-icon-header-clickable" />
                </div>
              </div>
              {/* Filter toggle */}
              <button
                className={`admin-action-btn border rounded px-2 py-1 ${showFilters ? 'admin-filter-active' : ''}`}
                onClick={() => setShowFilters((v) => !v)}
                title="Filters"
              >
                <Filter className="h-4 w-4" />
              </button>
              {/* Create */}
              <button
                className="admin-action-btn border rounded px-2 py-1"
                onClick={() => setCreateOpen(true)}
                title="Create Candidate"
              >
                <Plus className="h-4 w-4" />
              </button>
              {/* View toggle */}
              <div className="flex border rounded overflow-hidden">
                <button
                  className={`px-2 py-1 text-xs flex items-center gap-1 ${view === 'list' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}
                  onClick={() => setView('list')}
                  title="List view"
                >
                  <List className="h-3.5 w-3.5" />
                </button>
                <button
                  className={`px-2 py-1 text-xs flex items-center gap-1 border-l ${view === 'pipeline' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}
                  onClick={() => setView('pipeline')}
                  title="Pipeline view"
                >
                  <Columns className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="admin-content-area" style={{ flex: 1, overflowY: 'auto', paddingBottom: 0 }}>
          {showFilters && (
            <CandidateFiltersV2
              filters={filters}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />
          )}

          {view === 'pipeline' ? (
            <div className="p-2">
              <CandidatePipelineBoardV2 candidates={candidates} />
            </div>
          ) : isLoading ? (
            <div className="admin-table-container">
              <table className="admin-table">
                <tbody>{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}</tbody>
              </table>
            </div>
          ) : candidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Search className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-base font-medium">No candidates found</p>
              <p className="text-sm mt-1">
                {debouncedSearch || Object.values(filters).some(Boolean)
                  ? 'Try adjusting your search or filters'
                  : 'Create your first candidate to get started'}
              </p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell">Name</th>
                    <th className="admin-table-cell">Title</th>
                    <th className="admin-table-cell">Company</th>
                    <th className="admin-table-cell">Email</th>
                    <th className="admin-table-cell">Phone</th>
                    <th className="admin-table-cell">Stage</th>
                    <th className="admin-table-cell">Owner</th>
                    <th className="admin-table-cell">Source</th>
                    <th className="admin-table-cell">Last Activity</th>
                    <th className="admin-table-cell">Created</th>
                    <th className="admin-table-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const fullName = [c.firstName, c.lastName].filter(Boolean).join(' ');
                    return (
                      <tr key={c._id} className="admin-table-row">
                        <td className="admin-table-cell">
                          <button
                            className="font-medium text-sm text-blue-600 hover:underline text-left"
                            onClick={() => setDrawerCandidateId(c._id)}
                          >
                            {fullName || 'Unnamed'}
                          </button>
                        </td>
                        <td className="admin-table-cell text-sm text-gray-600">{c.currentTitle || '—'}</td>
                        <td className="admin-table-cell text-sm text-gray-600">{c.currentCompany || '—'}</td>
                        <td className="admin-table-cell text-sm text-gray-600">{c.email || '—'}</td>
                        <td className="admin-table-cell text-sm text-gray-600">{c.phone || '—'}</td>
                        <td className="admin-table-cell">
                          {c.stage ? <StageBadge stage={c.stage} /> : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="admin-table-cell text-sm text-gray-600">
                          {c.owner?.name || c.owner?.email || '—'}
                        </td>
                        <td className="admin-table-cell text-sm text-gray-600 capitalize">{c.source || '—'}</td>
                        <td className="admin-table-cell text-xs text-gray-400">{fmtDate(c.lastActivityAt)}</td>
                        <td className="admin-table-cell text-xs text-gray-400">{fmtDate(c.createdAt)}</td>
                        <td className="admin-table-cell text-right">
                          <ActionsDropdown
                            candidate={c}
                            onView={() => setDrawerCandidateId(c._id)}
                            onEdit={() => setEditCandidate(c)}
                            onArchive={() => archiveMutation.mutate(c._id)}
                            onRestore={() => restoreMutation.mutate(c._id)}
                            onMoveStage={(stage) => moveStageMutation.mutate({ id: c._id, stage })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer pagination */}
        {view === 'list' && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '8px', padding: '10px 20px',
            borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
            flexShrink: 0, fontSize: '13px', color: '#374151',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 500 }}>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(1); }}
                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', backgroundColor: '#fff', cursor: 'pointer' }}
              >
                {ROWS_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '4px 12px', borderRadius: '4px', fontSize: '12px', border: '1px solid #d1d5db', backgroundColor: page <= 1 ? '#f3f4f6' : '#fff', color: page <= 1 ? '#9ca3af' : '#374151', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
              >‹ Prev</button>
              <span style={{ padding: '4px 8px', fontSize: '12px' }}>{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '4px 12px', borderRadius: '4px', fontSize: '12px', border: '1px solid #d1d5db', backgroundColor: page >= totalPages ? '#f3f4f6' : '#fff', color: page >= totalPages ? '#9ca3af' : '#374151', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
              >Next ›</button>
            </div>

            <span style={{ fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>
              {total > 0 ? `Showing ${startIdx}–${endIdx} of ${total}` : 'No results'}
            </span>
          </div>
        )}
      </div>

      {/* Modals & Drawer */}
      <CreateCandidateModalV2 open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditCandidateModalV2
        open={!!editCandidate}
        candidate={editCandidate}
        onClose={() => setEditCandidate(null)}
      />
      {drawerCandidateId && (
        <CandidateDrawerV2
          candidateId={drawerCandidateId}
          onClose={() => setDrawerCandidateId(null)}
          onEdit={(c) => { setDrawerCandidateId(null); setEditCandidate(c); }}
        />
      )}
    </>
  );
};

export default CandidateListV2;
